import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  Button,
  ConfirmDialog,
  Drawer,
  EmptyState,
  GlassCard,
  GlassTable,
  StatusPill,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import { PAY_RUN_STATUS_KEY, PAY_RUN_STATUS_PILL } from '../../lib/label_keys';
import { usePayRuns } from '../../data/use_pay_runs';
import { usePayRunAudit } from '../../data/use_pay_run_audit';
import {
  format_date,
  format_month_year,
  format_money,
  format_time,
} from '../../lib/format';
import type { PayRun, PayRunAuditAction } from '../../data/types';

const AUDIT_KEY = {
  run_created: 'pay_run.audit.run_created',
  exceptions_acknowledged: 'pay_run.audit.exceptions_acknowledged',
  submitted_for_approval: 'pay_run.audit.submitted_for_approval',
  approved: 'pay_run.audit.approved',
  paid: 'pay_run.audit.paid',
  bank_file_generated: 'pay_run.audit.bank_file_generated',
  statutory_filing_submitted: 'pay_run.audit.statutory_filing_submitted',
} as const;

/**
 * The audit drawer's body — separated from `PayrollHistoryScreen` so
 * `usePayRunAudit(runId, runCreatedAt)` (C10, #22332) is only ever called
 * with a REAL, non-null pay-run id and its own creation timestamp: this
 * component only mounts while the `Drawer` is
 * open for a specific run (`open_run !== null` in the parent), so the hook
 * never has to represent "closed" as one of its own states. Fetches
 * on-demand (only once mounted for this specific run), never at
 * `PayrollHistoryScreen`'s own mount time for every run in the table.
 */
function AuditDrawerBody({
  runId,
  runCreatedAt,
  locale,
}: {
  runId: string;
  runCreatedAt: string;
  locale: string;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const { state, retry } = usePayRunAudit(runId, runCreatedAt);

  if (state.kind === 'loading') {
    return (
      <div className="flex flex-col gap-2.5" role="status">
        <span className="sr-only">{t('history.audit_loading')}</span>
        <div className="nv-skel" style={{ height: 52 }} />
        <div className="nv-skel" style={{ height: 52 }} />
      </div>
    );
  }

  if (state.kind === 'no_entity') {
    return <EmptyState role="status" title={t('history.audit_no_entity')} />;
  }

  if (state.kind === 'entitlement_required') {
    return (
      <EmptyState
        role="status"
        title={t('history.audit_entitlement_required')}
      />
    );
  }

  // `error` (fetch failed) and `empty` (fetch succeeded, zero real events)
  // are deliberately DIFFERENT components with different visual weight — an
  // `Alert` (danger) for the failure the user can retry, a plain
  // `EmptyState` for the legitimate "nothing happened yet" case. Collapsing
  // these into one "no rows" branch would make a failed fetch
  // indistinguishable from a brand-new pay run with no audit history, which
  // is exactly the bug this pass exists to fix.
  if (state.kind === 'error') {
    return (
      <Alert variant="danger" title={t('history.audit_error')}>
        <Button variant="secondary" size="xs" onClick={retry}>
          {t('history.audit_retry')}
        </Button>
      </Alert>
    );
  }

  // `empty_or_incomplete` (adversarial-review follow-up to C10, #22378) is
  // deliberately a THIRD, visually distinct state from both `empty` and
  // `error`: the search hit its bounded page cap before it could either
  // find this run's entries or safely rule out that any exist (see
  // `resolvePayRunAuditState`'s doc comment). Rendering this as a plain
  // `empty` would repeat the exact false "no audit events" claim this whole
  // feature exists to eliminate, just for a different underlying reason.
  if (state.kind === 'empty_or_incomplete') {
    return <EmptyState role="status" title={t('history.audit_incomplete')} />;
  }

  if (state.kind === 'empty') {
    return <EmptyState role="status" title={t('history.audit_empty')} />;
  }

  return (
    <div className="flex flex-col gap-2.5">
      {state.entries.map((entry) => (
        <div
          key={entry.id}
          className="rounded-lg border border-[color:var(--line)] p-2.5 text-[13px]"
        >
          <p className="font-semibold">{describe_audit(entry.action, t)}</p>
          <p className="text-[12px] text-[color:var(--ink-muted)] mt-0.5">
            {t('history.audit_meta', {
              actor: entry.actor,
              date: format_date(entry.at.slice(0, 10), locale),
              time: format_time(entry.at, locale),
            })}
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * The one dialog `PayrollHistoryScreen` opens for either void OR delete —
 * mutually exclusive per row (a `draft` can only be deleted, an `approved`
 * can only be voided; every other status offers neither). Kept as a single
 * union state rather than two independent `open`/`open_delete_id` flags so
 * the dialog can't be double-opened, and every close path clears the same
 * field.
 */
type PendingLifecycleAction =
  | { kind: 'void'; run: PayRun }
  | { kind: 'delete'; run: PayRun };

/** Completed and in-flight pay runs, each with its audit trail. */
export function PayrollHistoryScreen(): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { state, retry, void_pay_run, delete_pay_run } = usePayRuns();
  const [open_run_id, set_open_run_id] = useState<string | null>(null);
  const [pending, set_pending] = useState<PendingLifecycleAction | null>(null);
  const [is_submitting, set_is_submitting] = useState(false);
  const [action_error, set_action_error] = useState<string | null>(null);

  // `no_entity` is unreachable in practice (see `use_pay_runs.ts`'s module
  // docblock) -- rendered as the same loading placeholder as `loading`
  // rather than a distinct screen, since `main.tsx`'s `AuthGate` already
  // gates the whole app on this case before this screen can ever mount.
  if (state.status === 'loading' || state.status === 'no_entity') {
    return (
      <>
        <ScreenHeader
          title={t('history.title')}
          description={t('history.subtitle')}
        />
        <GlassCard variant="accent" className="rounded-2xl p-5">
          <span className="sr-only">{t('pay_run.loading')}</span>
          <div className="nv-skel" style={{ height: 44, margin: 8 }} />
          <div className="nv-skel" style={{ height: 44, margin: 8 }} />
          <div className="nv-skel" style={{ height: 44, margin: 8 }} />
        </GlassCard>
      </>
    );
  }

  if (state.status === 'entitlement_required') {
    return (
      <>
        <ScreenHeader
          title={t('history.title')}
          description={t('history.subtitle')}
        />
        <GlassCard variant="accent" className="rounded-2xl p-5">
          <EmptyState
            title={t('pay_run.entitlement_required_title')}
            description={t('pay_run.entitlement_required_description')}
          />
        </GlassCard>
      </>
    );
  }

  if (state.status === 'error') {
    return (
      <>
        <ScreenHeader
          title={t('history.title')}
          description={t('history.subtitle')}
        />
        <GlassCard variant="accent" className="rounded-2xl p-5">
          <EmptyState
            title={t('pay_run.load_error_title')}
            description={t('pay_run.load_error_description')}
            action={
              <Button variant="secondary" onClick={retry}>
                {t('pay_run.retry')}
              </Button>
            }
          />
        </GlassCard>
      </>
    );
  }

  const { pay_runs } = state;
  const open_run = pay_runs.find((run) => run.id === open_run_id) ?? null;

  return (
    <>
      <ScreenHeader
        title={t('history.title')}
        description={t('history.subtitle')}
      />

      <GlassTable>
        <table className="nv-table">
          <thead>
            <tr>
              <th scope="col">{t('history.column_period')}</th>
              <th scope="col" className="text-end">
                {t('history.column_gross')}
              </th>
              <th scope="col" className="text-end">
                {t('history.column_net')}
              </th>
              <th scope="col">{t('history.column_status')}</th>
              <th scope="col">
                <span className="sr-only">{t('common.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pay_runs.map((run) => (
              <tr key={run.id}>
                <td className="font-semibold">
                  {format_month_year(run.period, locale)}
                </td>
                {(run.blocked_contractor_count ?? 0) > 0 ? (
                  // A draft blocked by a worker now recorded as a contractor
                  // has placeholder-zero totals — never print them as money.
                  <td
                    colSpan={2}
                    className="text-end nv-help"
                    data-testid="history-row-contractor-blocked"
                  >
                    {t('pay_run.blocked_contractor_short')}
                  </td>
                ) : (
                  <>
                    <td className="text-end num">
                      {format_money(run.gross_wages, run.currency, locale)}
                    </td>
                    <td className="text-end num">
                      {format_money(run.net_payable, run.currency, locale)}
                    </td>
                  </>
                )}
                <td>
                  <StatusPill
                    status={PAY_RUN_STATUS_PILL[run.status]}
                    label={t(PAY_RUN_STATUS_KEY[run.status])}
                  />
                </td>
                <td className="text-end">
                  {/* `shrink-0 whitespace-nowrap` on every action button here,
                      not just a table-width tweak: this `<td>` already has
                      enough room (confirmed empirically, Playwright at
                      384px) -- what was actually squeezing "View audit
                      trail" onto 3 lines was the buttons' own flex-shrink
                      default (1) inside this row's flex wrapper, the exact
                      failure mode already fixed once for `.wp-step-label`
                      (see wagepro-theme.css). `GlassTable` already wraps
                      this table in `overflow-x-auto`, so refusing to shrink
                      here just lets the row scroll a little further
                      instead of wrapping its own button text. */}
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="secondary"
                      size="xs"
                      className="shrink-0 whitespace-nowrap"
                      onClick={() => {
                        set_open_run_id(run.id);
                      }}
                    >
                      {t('history.view_audit_trail')}
                    </Button>
                    {run.status === 'approved' &&
                      void_pay_run !== undefined && (
                        <Button
                          variant="ghost-danger"
                          size="xs"
                          className="shrink-0 whitespace-nowrap"
                          onClick={() => {
                            set_action_error(null);
                            set_pending({ kind: 'void', run });
                          }}
                        >
                          {t('history.void_cta')}
                        </Button>
                      )}
                    {run.status === 'draft' && delete_pay_run !== undefined && (
                      <Button
                        variant="ghost-danger"
                        size="xs"
                        className="shrink-0 whitespace-nowrap"
                        onClick={() => {
                          set_action_error(null);
                          set_pending({ kind: 'delete', run });
                        }}
                      >
                        {t('history.delete_cta')}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </GlassTable>

      <Drawer
        open={open_run !== null}
        onClose={() => {
          set_open_run_id(null);
        }}
        title={
          open_run === null
            ? t('history.audit_trail')
            : t('history.audit_trail_for', {
                period: format_month_year(open_run.period, locale),
              })
        }
      >
        {open_run !== null && (
          <AuditDrawerBody
            runId={open_run.id}
            runCreatedAt={open_run.created_at}
            locale={locale}
          />
        )}
      </Drawer>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            set_pending(null);
            set_is_submitting(false);
            set_action_error(null);
          }
        }}
        // Plain literal `t('...')` per branch (never
        // `t(cond ? 'a' : 'b')`) — the static i18n extractor
        // (`scripts/check-i18n-catalog-membership.ts`) reads `t()` calls with
        // a string-literal first arg only; a ternary makes the key look
        // dynamic and mis-flags it as an orphan. See that script's docblock.
        title={
          pending?.kind === 'delete'
            ? t('history.delete_confirm_title')
            : t('history.void_confirm_title')
        }
        description={
          pending === null
            ? ''
            : pending.kind === 'delete'
              ? t('history.delete_confirm_description', {
                  period: format_month_year(pending.run.period, locale),
                })
              : t('history.void_confirm_description', {
                  period: format_month_year(pending.run.period, locale),
                })
        }
        confirmLabel={
          pending?.kind === 'delete'
            ? t('history.delete_cta')
            : t('history.void_cta')
        }
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={is_submitting}
        onConfirm={() => {
          if (pending === null) return;
          const mutation =
            pending.kind === 'delete' ? delete_pay_run : void_pay_run;
          if (mutation === undefined) return;
          const errorMessage =
            pending.kind === 'delete'
              ? t('history.delete_error')
              : t('history.void_error');
          set_is_submitting(true);
          set_action_error(null);
          void mutation(pending.run.id)
            .then((result) => {
              if (result.outcome === 'success') {
                set_pending(null);
              } else {
                set_action_error(errorMessage);
              }
            })
            .finally(() => {
              set_is_submitting(false);
            });
        }}
      >
        {action_error !== null && (
          <p role="alert" className="nv-help is-error">
            {action_error}
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}

function describe_audit(
  action: PayRunAuditAction,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  return action.kind === 'exceptions_acknowledged'
    ? t(AUDIT_KEY[action.kind], { count: action.count })
    : t(AUDIT_KEY[action.kind]);
}
